import { ExternalLink, Building2, Star, ArrowUpRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { usePostEngagers } from "@/hooks/useCompanyTracking";
import { format } from "date-fns";
import type { PostCard } from "@/types/company-tracking";

import { ensureHttps, openExternal } from "@/lib/utils";

interface PostDetailModalProps {
  post: PostCard | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const PostDetailModal = ({ post, open, onOpenChange }: PostDetailModalProps) => {
  const navigate = useNavigate();
  const { data: engagers, isLoading } = usePostEngagers(post?.post_url || null);

  if (!post) return null;

  const handleViewAccount = (accountId: string) => {
    onOpenChange(false);
    navigate(`/accounts/${accountId}`);
  };

  const formattedDate = post.post_date
    ? format(new Date(post.post_date), "MMMM d, yyyy 'at' h:mm a")
    : "Unknown date";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Post by {post.monitoring_profile}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-hidden">
          <ScrollArea className="h-[calc(85vh-100px)]">
            <div className="space-y-4 pr-4">
            {/* Post Content */}
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">{formattedDate}</p>
              <p className="text-sm whitespace-pre-wrap">{post.post_content}</p>
              <Button variant="outline" size="sm" asChild>
                <a
                  href={ensureHttps(post.post_url) ?? post.post_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2"
                >
                  <ExternalLink className="h-4 w-4" />
                  View on LinkedIn
                </a>
              </Button>
            </div>

            {/* Engagement Stats */}
            <div className="flex gap-4 text-sm">
              <span>❤️ {post.likes} likes</span>
              <span>💬 {post.comments} comments</span>
              <span>👥 {post.engagement_count} total engagements</span>
            </div>

            {/* Engagers Table */}
            <div>
              <h4 className="text-sm font-medium mb-2">
                Who Engaged {engagers?.length ? <span className="text-muted-foreground">({engagers.length})</span> : null}
              </h4>
              <div className="border rounded-md">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Job Title</TableHead>
                      <TableHead>Company</TableHead>
                      <TableHead>Type</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {isLoading ? (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center py-8">
                          Loading engagers...
                        </TableCell>
                      </TableRow>
                    ) : engagers?.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                          No engager data available
                        </TableCell>
                      </TableRow>
                    ) : (
                      engagers?.map((engager, idx) => (
                        <TableRow key={idx}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {ensureHttps(engager.prospect_linkedin_url) ? (
                                <button
                                  type="button"
                                  onClick={() => openExternal(engager.prospect_linkedin_url)}
                                  className="text-primary hover:underline"
                                >
                                  {engager.prospect_name || "Unknown"}
                                </button>
                              ) : (
                                <span>{engager.prospect_name || "Unknown"}</span>
                              )}
                              {engager.is_top_account && (
                                <Star className="h-3.5 w-3.5 text-yellow-500 fill-yellow-500" />
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {engager.prospect_job_title || "-"}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1.5">
                              <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                              {engager.matched_account_id ? (
                                <button
                                  onClick={() => handleViewAccount(engager.matched_account_id!)}
                                  className="text-primary hover:underline flex items-center gap-1"
                                >
                                  {engager.company_name || "-"}
                                  <ArrowUpRight className="h-3 w-3" />
                                </button>
                              ) : (
                                <span>{engager.company_name || "-"}</span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant={engager.engagement_type === "comment" ? "default" : "secondary"}
                            >
                              {engager.engagement_type === "comment" ? "Comment" : engager.engagement_type === "like" ? "Like" : (engager.engagement_type || "Like")}
                            </Badge>
                            {engager.comment_text && (
                              <p className="text-xs text-muted-foreground mt-1 max-w-[200px] truncate">
                                "{engager.comment_text}"
                              </p>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
            </div>
          </ScrollArea>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PostDetailModal;
